// "Why this account?": every request a person makes carries a reason (Vikram, 7 Oct).
//
// The server refuses a request without one (`board/actions.request_reason_or_400`, a 400 saying
// "Say why you want this account."); these are the same rule in the browser, so the button stays
// off until there is a reason, and an upload's requests are announced in Slack as one batch.

import type { WriteActionResult } from '../api/types';

export const REQUEST_REASON_LABEL = 'Why this account?';
export const REQUEST_REASON_PLACEHOLDER = 'e.g. hiring voice engineers, just raised a Series B';
export const MAX_REQUEST_REASON_LENGTH = 500;

/** The reason as it will be sent, or null when it is empty (the request button stays off). */
export function requestReason(text: string): string | null {
  const trimmed = text.trim();
  return trimmed ? trimmed.slice(0, MAX_REQUEST_REASON_LENGTH) : null;
}

/** One upload row's reason: the list's "why", plus the row's own evidence when the CSV gave one. */
export function rowRequestReason(why: string, rowEvidence: string, defaultEvidence: string): string | null {
  const reason = requestReason(why);
  if (reason === null) return null;
  const evidence = rowEvidence.trim();
  if (!evidence || evidence === defaultEvidence) return reason;
  return requestReason(`${reason} (${evidence})`);
}

export interface RequestedRef {
  account_id: string;
  owner: string;
}

// A decline's reason is optional (Vikram's test pass, 7 Oct): the queue's Decline sends what was
// typed, or nothing, and the server stores no reason as none.
export const DECLINE_REASON_PLACEHOLDER = 'Reason (optional)';

/** The queue's line for a decline: "Declined Acme · Vikram: Not a fit", no colon without a reason. */
export function declinedQueueLine(name: string, declinedBy: string, reason: string | null | undefined): string {
  const trimmed = (reason || '').trim();
  return `Declined ${name} · ${declinedBy}${trimmed ? `: ${trimmed}` : ''}`;
}

/** The requests among an upload's answers, for its one Slack announcement. */
export function requestedRefs(answers: WriteActionResult[]): RequestedRef[] {
  const refs: RequestedRef[] = [];
  for (const answer of answers) {
    if (answer.outcome !== 'requested' || !answer.account_id || !answer.owner) continue;
    refs.push({ account_id: answer.account_id, owner: answer.owner });
  }
  return refs;
}
